import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';

import { FormlyFieldTextArea } from '@ngx-formly/ng-zorro-antd/textarea';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  NzUploadChangeParam,
  NzUploadFile,
  NzUploadXHRArgs,
} from 'ng-zorro-antd/upload';
import { UserService } from '../../../user.service';

import { AngularFireStorage } from '@angular/fire/compat/storage';
import { UploadTaskSnapshot } from '@angular/fire/compat/storage/interfaces';
import { AngularFireUploadTask } from '@angular/fire/compat/storage/task';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  filter,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

type TextAreaProps = FormlyFieldProps;

type FileType = 'image' | 'pdf' | 'file';

export interface FormlyTextAreaFieldConfig
  extends FormlyFieldConfig<TextAreaProps> {
  type: 'file' | Type<FormlyFieldTextArea>;
}

@Component({
  selector: 'etoh-upload',
  styles: [``],
  template: `
    <nz-upload
      nzType="drag"
      [nzMultiple]="true"
      [nzFileList]="fileList()"
      [nzListType]="'picture'"
      (nzChange)="handleChange($event)"
      [nzCustomRequest]="uploadFile"
      [nzPreview]="handlePreview"
    >
      <p class="ant-upload-drag-icon">
        <i nz-icon nzType="inbox"></i>
      </p>
      <p class="ant-upload-text">Click or drag file to this area to upload</p>
      <p class="ant-upload-hint">Support for a single or bulk upload.</p>
    </nz-upload>

    <nz-modal
      [nzWidth]="previewFileType === 'pdf' ? 800 : 600"
      [nzVisible]="previewVisible"
      [nzContent]="modalContent"
      [nzFooter]="null"
      (nzOnCancel)="closePreview()"
    >
      <ng-template #modalContent>
        <div class="modal-container" [ngClass]="previewFileType">
          @if (previewFileType === 'pdf') {
          <iframe
            [src]="previewUrl"
            style="width: 100%; height:700px;"
            frameborder="0"
          ></iframe>
          } @else if (previewFileType === 'image') {
          <img [src]="previewUrl" [ngStyle]="{ width: '100%' }" />
          } @else { No preview available }
        </div>
      </ng-template>
    </nz-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldUpload
  extends FieldType<FieldTypeConfig<TextAreaProps>>
  implements OnInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly destroy$ = new Subject<void>();
  private readonly persistedFiles = signal<NzUploadFile[]>([]);
  private readonly pendingFiles = signal<Record<string, NzUploadFile>>({});

  readonly fileList = computed(() => {
    const pendingFiles = this.pendingFiles();
    const pendingUids = new Set(Object.keys(pendingFiles));

    const persistedFiles = this.persistedFiles()
      .filter((file) => !pendingUids.has(file.uid))
      .map((file) => this.toDisplayFile(file));

    return [...persistedFiles, ...Object.values(pendingFiles)];
  });

  private activeUploads = new Map<string, AngularFireUploadTask>();
  private activeUploadSubscriptions = new Map<string, Subscription>();

  get value(): NzUploadFile[] {
    return this.formControl.value ?? [];
  }

  constructor(
    private msg: NzMessageService,
    private userService: UserService,
    private storage: AngularFireStorage
  ) {
    super();
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(startWith(this.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: NzUploadFile[]) => {
        this.persistedFiles.set(value ?? []);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    Array.from(this.activeUploads.keys()).forEach((uploadId) => {
      this.cancelUpload(uploadId);
    });
  }

  handleChange({ file }: NzUploadChangeParam): void {
    const status = file.status;

    if (status === 'done') {
      this.msg.success(`${file.name} file uploaded successfully.`);
    } else if (status === 'removed') {
      this.cancelUpload(file.uid);

      const valuesWithoutRemoved = this.value.filter(
        (value) => value.uid !== file.uid
      );

      this.setFormControlValue(valuesWithoutRemoved);
    } else if (status === 'error') {
      this.removePendingUpload(file.uid);
      this.msg.error(`${file.name} file upload failed.`);
    }
  }

  uploadFile = (item: NzUploadXHRArgs) => {
    const file = item.file;
    const uploadId = file.uid;

    this.cancelUpload(uploadId);

    const filePath = `${this.userService.userId$.value}/${file.uid}`;
    const fileRef = this.storage.ref(filePath);
    const task = this.storage.upload(filePath, file);
    this.activeUploads.set(uploadId, task);
    this.setPendingUpload({
      uid: file.uid,
      name: file.name,
      type: file.type,
      size: file.size,
      status: 'uploading',
      percent: 0,
      thumbUrl: this.getThumbnail(file),
    });

    const uploadSubscription = task
      .snapshotChanges()
      .pipe(
        filter((result): result is UploadTaskSnapshot => !!result),
        tap((result) => {
          const event = { percent: 0 };
          event.percent = (result.bytesTransferred / result.totalBytes) * 100;
          this.setPendingUpload({
            uid: file.uid,
            name: file.name,
            type: file.type,
            size: file.size,
            status: 'uploading',
            percent: event.percent,
            thumbUrl: this.getThumbnail(file),
          });
          item.onProgress?.(event, item.file);
        }),
        filter((result) => result.bytesTransferred === result.totalBytes),
        take(1),
        switchMap(() => fileRef.getDownloadURL().pipe(take(1))),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          const fileToSave: NzUploadFile = {
            uid: file.uid,
            name: file.name,
            type: file.type,
            size: file.size,
            url: result,
          };

          item.onSuccess?.(
            result,
            {
              ...fileToSave,
              status: 'done',
              thumbUrl: this.getThumbnail(fileToSave),
            },
            result
          );

          const files = [...this.value, fileToSave];

          this.setFormControlValue(files);
          this.removePendingUpload(uploadId);
        },
        error: (err) => {
          this.removePendingUpload(uploadId);
          item.onError?.(err, item.file);
        },
      });
    uploadSubscription.add(() => {
      this.releaseUpload(uploadId);
    });

    this.activeUploadSubscriptions.set(uploadId, uploadSubscription);

    return uploadSubscription;
  };

  private toDisplayFile(file: NzUploadFile): NzUploadFile {
    return {
      ...file,
      thumbUrl: this.getThumbnail(file),
      status: file.status ?? 'done',
    };
  }

  private setPendingUpload(file: NzUploadFile): void {
    this.pendingFiles.update((pendingFiles) => ({
      ...pendingFiles,
      [file.uid]: file,
    }));
  }

  private setFormControlValue(files: NzUploadFile[]): void {
    this.formControl.setValue(files, { emitEvent: true });
  }

  private removePendingUpload(uploadId: string): void {
    this.pendingFiles.update((pendingFiles) => {
      if (!pendingFiles[uploadId]) {
        return pendingFiles;
      }

      const updatedPendingFiles = { ...pendingFiles };
      delete updatedPendingFiles[uploadId];
      return updatedPendingFiles;
    });
  }

  private releaseUpload(uploadId: string) {
    this.activeUploadSubscriptions.delete(uploadId);
    this.activeUploads.delete(uploadId);
  }

  private cancelUpload(uploadId: string) {
    const uploadTask = this.activeUploads.get(uploadId);
    const uploadSubscription = this.activeUploadSubscriptions.get(uploadId);

    uploadSubscription?.unsubscribe();
    uploadTask?.cancel();
    this.removePendingUpload(uploadId);
    this.releaseUpload(uploadId);
  }

  private getThumbnail(file: NzUploadFile): string {
    const typeFile = this.getFileType(file);

    if (typeFile === 'pdf') {
      return '/assets/icons/files/pdf.png';
    } else if (typeFile === 'image') {
      return (
        file.thumbUrl ??
        (file.url as string | undefined) ??
        '/assets/icons/files/file.png'
      );
    } else {
      return '/assets/icons/files/file.png';
    }
  }

  private getFileType(file: NzUploadFile): FileType {
    const typeFileExtension = file.name.split('.').pop();

    if (typeFileExtension === 'pdf') {
      return 'pdf';
    } else if (
      ['jpg', 'jpeg', 'png', 'gif'].includes(typeFileExtension ?? 'eazeaz')
    ) {
      return 'image';
    }

    return 'file';
  }

  previewUrl: SafeResourceUrl | undefined = undefined;
  previewFileType: FileType | undefined = undefined;
  previewVisible = false;

  private sanitizer = inject(DomSanitizer);

  handlePreview = async (file: NzUploadFile): Promise<void> => {
    // const preview = await getBase64(file.originFileObj!);

    this.previewFileType = this.getFileType(file);

    if (this.previewFileType === 'pdf') {
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        file.url as string
      );
    } else {
      this.previewUrl = file.thumbUrl ?? file.url;
    }

    this.previewVisible = true;
  };

  closePreview(): void {
    this.previewVisible = false;
    this.previewUrl = undefined;
    this.previewFileType = undefined;
  }
}
